/**
 * 보안 메모 메인 컴포넌트
 * 폴더 사이드바와 메모 목록 통합
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSecureNotes } from '../../contexts/SecureNotesContext';
import { useFolders } from '../../contexts/FolderContext';
import { usePin } from '../../contexts/PinContext';
import FolderSidebar from './FolderSidebar';
import SecureNoteCard from './SecureNoteCard';
import SecureNoteForm from './SecureNoteForm';
import PinModal from './PinModal';
import SecureNoteViewModal from './SecureNoteViewModal';
import FolderSelectModal from './FolderSelectModal';
import { SearchBox, ActionButton, FilterLayout, Button } from '../UI';
import {
  Plus,
  FileText,
  RefreshCw,
  Menu,
  X
} from 'lucide-react';
import './SecureNotes.css';

function SecureNotes() {
  const {
    notes,
    loading,
    error,
    filters,
    updateFilters,
    resetFilters,
    loadNotes,
    getNote,
    deleteNote
  } = useSecureNotes();

  const {
    selectedFolder,
    selectFolder,
    getFolderInfo,
    moveNote,
    folderTree,
    SYSTEM_FOLDERS
  } = useFolders();

  const { verifyPin, checkUnlockStatus, pinSettings } = usePin();

  const [showForm, setShowForm] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinAction, setPinAction] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showSidebar, setShowSidebar] = useState(window.innerWidth > 768);
  const [focusedFolderIndex, setFocusedFolderIndex] = useState(0);
  const [movingNote, setMovingNote] = useState(null);
  const [showFolderSelectModal, setShowFolderSelectModal] = useState(false);
  const [moveSuccess, setMoveSuccess] = useState(false);

  // 화면 크기 감지
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      // 데스크톱에서는 항상 사이드바 표시
      if (!mobile) {
        setShowSidebar(true);
      }
    };

    // 초기 로드 시에도 실행
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  // 키보드 단축키 처리
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd/Ctrl + K: 검색 포커스
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        document.querySelector('.search-box input')?.focus();
        return;
      }

      // Cmd/Ctrl + N: 새 메모
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault();
        setEditingNote(null);
        setShowForm(true);
        return;
      }

      // Cmd/Ctrl + B: 사이드바 토글 (모바일에서만)
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        if (isMobile) {
          setShowSidebar(prev => !prev);
        }
        return;
      }

      // 폴더 네비게이션 (1-9 키)
      if (!e.metaKey && !e.ctrlKey && !e.altKey) {
        // 포커스된 입력 필드가 있으면 무시
        if (document.activeElement.tagName === 'INPUT' ||
            document.activeElement.tagName === 'TEXTAREA') {
          return;
        }

        const num = parseInt(e.key);
        if (num >= 1 && num <= 9) {
          // 폴더 트리를 플랫하게 만들기
          const flatFolders = [];
          const flattenTree = (folders) => {
            folders.forEach(folder => {
              flatFolders.push(folder);
              if (folder.children) {
                flattenTree(folder.children);
              }
            });
          };
          flattenTree(folderTree);

          // 해당 인덱스의 폴더 선택
          if (flatFolders[num - 1]) {
            selectFolder(flatFolders[num - 1].id);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [folderTree, selectFolder, setShowForm, setEditingNote]);

  // Note: We don't reload notes when folder changes - filtering is done client-side

  // 필터링된 메모 목록 (폴더별) - 디버깅 로그 추가
  const filteredNotes = useMemo(() => {
    console.log('Filtering notes:', {
      totalNotes: notes.length,
      selectedFolder,
      searchTerm,
      filters
    });

    let filtered = [...notes];

    // 삭제된 메모 제외 (휴지통 제외)
    if (selectedFolder !== SYSTEM_FOLDERS.TRASH) {
      filtered = filtered.filter(note => !note.isDeleted);
    }

    // 폴더 필터
    if (!selectedFolder || selectedFolder === SYSTEM_FOLDERS.ALL) {
      // 모든 메모 표시 (삭제되지 않은 것만)
      console.log('Showing all notes:', filtered.length);
    } else if (selectedFolder === SYSTEM_FOLDERS.FAVORITES) {
      // 즐겨찾기
      filtered = filtered.filter(note => note.isFavorite === true);
      console.log('Favorites filtered:', filtered.length);
    } else if (selectedFolder === SYSTEM_FOLDERS.RECENT) {
      // 최근 7일 이내
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      filtered = filtered.filter(note => {
        if (!note.updatedAt) return false;
        const updatedAt = note.updatedAt?.toDate ? note.updatedAt.toDate() : new Date(note.updatedAt);
        return updatedAt > sevenDaysAgo;
      });
      console.log('Recent filtered:', filtered.length);
    } else if (selectedFolder === SYSTEM_FOLDERS.ARCHIVED) {
      // 보관함
      filtered = filtered.filter(note => note.isArchived === true);
      console.log('Archived filtered:', filtered.length);
    } else if (selectedFolder === SYSTEM_FOLDERS.TRASH) {
      // 휴지통
      filtered = filtered.filter(note => note.isDeleted === true);
      console.log('Trash filtered:', filtered.length);
    } else {
      // 사용자 폴더
      const beforeFilter = filtered.length;
      filtered = filtered.filter(note => note.folderId === selectedFolder);
      console.log(`User folder ${selectedFolder} filtered: ${beforeFilter} → ${filtered.length}`);
    }

    // 검색어 필터
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      const beforeSearch = filtered.length;
      filtered = filtered.filter(note =>
        note.title?.toLowerCase().includes(lower) ||
        note.plainTextContent?.toLowerCase().includes(lower) ||
        note.tags?.some(tag => tag.toLowerCase().includes(lower))
      );
      console.log(`Search filtered: ${beforeSearch} → ${filtered.length}`);
    }

    // 카테고리 필터
    if (filters.category) {
      const beforeCategory = filtered.length;
      filtered = filtered.filter(note => note.category === filters.category);
      console.log(`Category filtered: ${beforeCategory} → ${filtered.length}`);
    }

    // 정렬
    filtered.sort((a, b) => {
      const order = filters.sortOrder === 'asc' ? 1 : -1;
      if (filters.sortBy === 'title') {
        return order * a.title.localeCompare(b.title);
      } else if (filters.sortBy === 'createdAt') {
        return order * (new Date(b.createdAt) - new Date(a.createdAt));
      } else {
        return order * (new Date(b.updatedAt) - new Date(a.updatedAt));
      }
    });

    console.log('Final filtered notes:', filtered.length);
    return filtered;
  }, [notes, searchTerm, filters, selectedFolder, SYSTEM_FOLDERS]);

  // 메모 보기
  const handleViewNote = async (note) => {
    try {

      // PIN 보호 확인
      if (note.isPinProtected) {
        const isUnlocked = await checkUnlockStatus();
        if (!isUnlocked) {
          setPinAction({
            type: 'view',
            noteId: note.id,
            callback: async (pin) => {
              const fullNote = await getNote(note.id, pin);
              setSelectedNote(fullNote);
            }
          });
          setShowPinModal(true);
          return;
        }
      }

      // If already unlocked or not protected, we can pass null for PIN
      const fullNote = await getNote(note.id, null);
      setSelectedNote(fullNote);
    } catch (err) {
      console.error('Error viewing note:', err);
    }
  };

  // 메모 편집
  const handleEditNote = async (note) => {
    try {
      // PIN 보호 확인
      if (note.isPinProtected) {
        const isUnlocked = await checkUnlockStatus();
        if (!isUnlocked) {
          setPinAction({
            type: 'edit',
            noteId: note.id,
            callback: async (pin) => {
              const fullNote = await getNote(note.id, pin);
              setEditingNote(fullNote);
              setShowForm(true);
            }
          });
          setShowPinModal(true);
          return;
        }
      }

      const fullNote = await getNote(note.id);
      setEditingNote(fullNote);
      setShowForm(true);
    } catch (err) {
      console.error('Error editing note:', err);
    }
  };

  // 메모 삭제
  const handleDeleteNote = async (noteId) => {
    if (selectedFolder === SYSTEM_FOLDERS.TRASH) {
      // 휴지통에서는 완전 삭제
      if (confirm('이 메모를 완전히 삭제하시겠습니까? 이 작업은 취소할 수 없습니다.')) {
        await deleteNote(noteId, true);
      }
    } else {
      // 휴지통으로 이동
      if (confirm('이 메모를 휴지통으로 이동하시겠습니까?')) {
        await deleteNote(noteId, false);
      }
    }
  };

  // 메모 이동
  const handleMoveNote = (note) => {
    setMovingNote(note);
    setShowFolderSelectModal(true);
  };

  // 폴더 선택 후 이동 처리
  const handleFolderSelect = async (targetFolderId) => {
    if (!movingNote) return;

    try {
      await moveNote(movingNote.id, targetFolderId);
      await loadNotes(); // 메모 목록 새로고침
      setMovingNote(null);
      setShowFolderSelectModal(false);

      // 성공 메시지 표시
      setMoveSuccess(true);
      setTimeout(() => setMoveSuccess(false), 3000);

      // 이동한 폴더로 자동 이동
      selectFolder(targetFolderId);
    } catch (error) {
      console.error('Error moving note:', error);
      alert('메모 이동 중 오류가 발생했습니다.');
    }
  };

  // 드래그 시작
  const handleDragStart = (e, noteId) => {
    e.dataTransfer.setData('noteId', noteId);
  };

  // 현재 폴더 정보
  const currentFolderInfo = getFolderInfo(selectedFolder);

  return (
    <div className="secure-notes-page">
      {/* 모바일 메뉴 버튼 */}
      {isMobile && (
        <button
          className="mobile-menu-btn"
          onClick={() => {
            if (isMobile) {
              console.log('Mobile menu clicked, current state:', showSidebar);
              setShowSidebar(!showSidebar);
            }
          }}
          aria-label={showSidebar ? '사이드바 닫기' : '사이드바 열기'}
        >
          {showSidebar ? <X size={24} /> : <Menu size={24} />}
        </button>
      )}

      {/* 폴더 사이드바 */}
      <>
        {isMobile && showSidebar && (
          <div
            className="sidebar-overlay"
            onClick={() => setShowSidebar(false)}
          />
        )}
        {!isMobile ? (
          <FolderSidebar isOpen={true} />
        ) : (
          <FolderSidebar isOpen={showSidebar} />
        )}
      </>

      {/* 메인 콘텐츠 */}
      <div className="secure-notes-content">
        {/* 헤더 */}
        <div className="secure-notes-header">
          <div className="header-title">
            <h2>
              {currentFolderInfo?.name || '보안 메모장'}
            </h2>
            <span className="notes-count">{filteredNotes.length}개</span>
          </div>

          <div className="header-actions">
            <ActionButton
              variant="secondary"
              size="icon-only"
              icon={RefreshCw}
              onClick={loadNotes}
              disabled={loading}
              title="새로고침"
              className={loading ? 'spinning' : ''}
            />
            <ActionButton
              variant="primary"
              size="md"
              icon={Plus}
              onClick={() => {
                setEditingNote(null);
                setShowForm(true);
              }}
              title="새 메모 (Cmd/Ctrl + N)"
            >
              새 메모
            </ActionButton>
          </div>
        </div>

        {/* 필터 */}
        <FilterLayout
          searchSlot={
            <SearchBox
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="메모 검색... (Cmd/Ctrl + K)"
            />
          }
          filterSlot={
            <>
              <select
                className="filter-select"
                value={filters.category || ''}
                onChange={(e) => updateFilters({ category: e.target.value })}
              >
                <option value="">카테고리</option>
                <option value="personal">개인</option>
                <option value="work">업무</option>
                <option value="ideas">아이디어</option>
                <option value="other">기타</option>
              </select>

              <select
                className="filter-select"
                value={filters.sortBy}
                onChange={(e) => updateFilters({ sortBy: e.target.value })}
              >
                <option value="updatedAt">수정일</option>
                <option value="createdAt">생성일</option>
                <option value="title">제목</option>
              </select>
            </>
          }
          className="secure-notes-filters"
        />

        {/* 에러 메시지 */}
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* 성공 메시지 */}
        {moveSuccess && (
          <div className="success-message">
            메모가 성공적으로 이동되었습니다.
          </div>
        )}

        {/* 로딩 */}
        {loading && !notes.length && (
          <div className="loading-container">
            <div className="spinner" />
            <p>메모를 불러오는 중...</p>
          </div>
        )}

        {/* 메모 목록 */}
        {!loading && filteredNotes.length === 0 ? (
          <div className="empty-state">
            <FileText size={64} />
            <h3>메모가 없습니다</h3>
            <p>새 메모를 작성하여 시작하세요</p>
            <Button onClick={() => setShowForm(true)}>
              <Plus size={20} />
              첫 메모 작성
            </Button>
          </div>
        ) : (
          <div className="notes-grid">
            {filteredNotes.map(note => (
              <div
                key={note.id}
                draggable
                onDragStart={(e) => handleDragStart(e, note.id)}
              >
                <SecureNoteCard
                  note={note}
                  onView={() => handleViewNote(note)}
                  onEdit={() => handleEditNote(note)}
                  onDelete={() => handleDeleteNote(note.id)}
                  onMove={() => handleMoveNote(note)}
                />
              </div>
            ))}
          </div>
        )}

        {/* 메모 작성/수정 폼 */}
        {showForm && (
          <SecureNoteForm
            note={editingNote}
            currentFolderId={selectedFolder !== SYSTEM_FOLDERS.ALL ? selectedFolder : null}
            onClose={() => {
              setShowForm(false);
              setEditingNote(null);
            }}
            onSave={() => {
              setShowForm(false);
              setEditingNote(null);
              loadNotes();
            }}
          />
        )}

        {/* PIN 모달 */}
        {showPinModal && pinAction && (
          <PinModal
            isOpen={showPinModal}
            onClose={() => {
              setShowPinModal(false);
              setPinAction(null);
            }}
            onSuccess={async (pin) => {
              try {
                const isValid = await verifyPin(pin);
                if (isValid) {
                  setShowPinModal(false);
                  if (pinAction.callback) {
                    await pinAction.callback(pin);
                  }
                  setPinAction(null);
                  return true;
                }
                return false;
              } catch (error) {
                console.error('Error during PIN verification:', error);
                return false;
              }
            }}
            title={pinAction.type === 'view' ? 'PIN 입력' : 'PIN 확인'}
            message="이 메모는 PIN으로 보호되어 있습니다."
          />
        )}

        {/* 메모 보기 모달 */}
        {selectedNote && (
          <SecureNoteViewModal
            note={selectedNote}
            isOpen={true}
            onClose={() => setSelectedNote(null)}
            onEdit={() => {
              handleEditNote(selectedNote);
              setSelectedNote(null);
            }}
          />
        )}

        {/* 폴더 선택 모달 */}
        <FolderSelectModal
          isOpen={showFolderSelectModal}
          onClose={() => {
            setShowFolderSelectModal(false);
            setMovingNote(null);
          }}
          onSelect={handleFolderSelect}
          currentFolderId={movingNote?.folderId || selectedFolder}
          noteTitle={movingNote?.title}
        />
      </div>
    </div>
  );
}

export default SecureNotes;